import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil } from 'rxjs/operators';
import moment from 'moment-timezone';

import {
    GableSkillDraft,
    GableSkillExceptionType,
    GableSkillFull,
    GableSkillInput,
    GableSkillOrgLookupResult,
    GableSkillReach,
    GableSkillReachException,
    GableSkillReachFilters,
    GableSkillReachMode,
    GableSkillRow,
    GableSkillSizeBand,
    GableSkillSource,
    GableSkillStarter,
    GableSkillsService,
} from '../../../services/gable/gable-skills.service';
import { GableDrawerService } from '../../../services/gable/gable-drawer.service';
import { LegFiJwtService } from '../../../services/auth/legfi-jwt.service';
import { GrowlerService } from '../../shared/growler/growler.service';
import { ConfirmDialogComponent } from '../../v2-layout/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { US_STATES } from '../../v2-layout/stripe-treasury/stripe-treasury.constants';
import { focusWhenMounted } from '../focus-when-mounted';

export type GableSkillsPanelView =
    | 'list'
    | 'detail'
    | 'edit'
    | 'add-upload'
    | 'add-write'
    | 'add-gable'
    | 'add-library'
    | 'reach';

interface SkillEditDraft {
    id: number | null;
    name: string;
    description: string;
    body: string;
    source: GableSkillSource;
    banner: string | null;
}

const NAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// Gable drawer SKILLS panel (SD-9197) — super-only global library management.
@UntilDestroy()
@Component({
    selector: 'app-gable-skills-panel',
    templateUrl: './gable-skills-panel.component.html',
    styleUrls: ['./gable-skills-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, ConfirmDialogComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class GableSkillsPanelComponent implements OnInit {
    @Output() skillsChanged = new EventEmitter<void>();
    @Output() switchToChat = new EventEmitter<void>();

    @ViewChild('replaceInput') replaceInputRef?: ElementRef<HTMLInputElement>;

    readonly usStates = US_STATES;

    readonly reachModeItems = [
        { value: 'all', label: 'All organizations', detail: 'Every organization on PayHOA, including ones that join later.' },
        { value: 'selected', label: 'Selected organizations', detail: 'Narrow it by state and size, with exceptions either way.' },
    ];

    get skillMenuItems(): { value: string; label: string; icon: string; destructive?: boolean }[] {
        // On and off is the switch on the page; the menu holds the rarer actions.
        return this._skillMenuRest;
    }

    private readonly _skillMenuRest = [
        { value: 'chat', label: 'Try it in chat', icon: 'chat-circle-text' },
        { value: 'edit-gable', label: 'Edit with Gable', icon: 'sparkle' },
        { value: 'replace', label: 'Replace file', icon: 'upload-simple' },
        { value: 'download', label: 'Download', icon: 'download-simple' },
        { value: 'remove', label: 'Remove', icon: 'trash', destructive: true },
    ];

    view: GableSkillsPanelView = 'list';

    skills: GableSkillRow[] = [];
    loading = false;

    /**
     * One-shot: true once the first list response has landed, never reset. Gates
     * the drafts child, which must survive the reloads an approval triggers —
     * `loading` is per-request and would unmount it mid-review.
     */
    listLoaded = false;

    /** Why the last offered file was refused, or null. */
    uploadError: string | null = null;

    /** Backs the confirm dialog for removing the selected skill. */
    removeConfirmOpen = false;

    /**
     * Whether the last list load failed. Without this term, a failed `GET skills`
     * renders the authoritative "No skills yet" block with three create CTAs to a
     * user who has forty skills — the toolbar, drafts, grid and helper all hidden
     * and no retry control, behind a growler that self-dismisses in 5 seconds.
     * Both sibling panels carry the same term (`feedError`, `preferencesLoadFailed`).
     */
    listLoadFailed = false;

    /**
     * True only when the list is genuinely empty, not when the search has
     * filtered it to nothing — a filtered-to-zero list keeps the compact header
     * and shows its own "no match" block. Gated on the load so the splash can't
     * flash on open.
     */
    get panelEmpty(): boolean {
        // `listLoaded` as well as `!loading`: the two are not the same window.
        // The first-load skeleton hangs on `listLoaded`, and before the fetch is
        // even issued `loading` is still false — so with only the `!loading`
        // term the empty-state splash rendered UNDERNEATH the skeleton for that
        // gap. Same rule as the rest of this view: one load state, then one
        // final state.
        return this.listLoaded && !this.loading && !this.listLoadFailed && this.skills.length === 0;
    }
    searchTerm = '';

    selected: GableSkillFull | null = null;
    detailLoading = false;

    edit: SkillEditDraft = this._blankDraft();
    editReach: GableSkillReach = this._blankReach();
    saving = false;

    briefText = '';
    drafting = false;
    private readonly _draftAbort$ = new Subject<void>();

    starters: GableSkillStarter[] = [];
    startersLoading = false;

    reach: GableSkillReach = this._blankReach();
    reachPreview: { count: number; total: number; examples: string[] } | null = null;
    reachPreviewLoading = false;
    reachConfirmZero = false;
    statesExpanded = false;
    stateSearch = '';
    bandCounts: Partial<Record<string, number>> = {};

    exceptionSearch = '';
    exceptionSearchLoading = false;
    exceptionResults: GableSkillOrgLookupResult[] = [];

    private _reachOrigin: 'edit' | 'detail' = 'edit';
    private _uploadDrop: File[] | null = null;
    private readonly _exceptionSearch$ = new Subject<string>();
    private readonly _reachPreviewTrigger$ = new Subject<void>();
    private readonly _bandCountsTrigger$ = new Subject<void>();

    constructor(
        private readonly _service: GableSkillsService,
        private readonly _drawer: GableDrawerService,
        private readonly _growler: GrowlerService,
        private readonly _cdr: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
    ) {}

    /** Reach is a PayHOA-library concept; an org admin's skills only ever apply to their org (SD-10602). */
    readonly isSuperUser = !!LegFiJwtService.read()?.superUser;

    /**
     * The views are `*ngSwitchCase` siblings, so a switch detaches whatever held
     * focus on the same change-detection pass and drops it to `<body>` — behind a
     * drawer with no focus trap, which restarts a keyboard user at the top of the
     * page. Hands focus to the new view's first control instead.
     *
     * Queried off the host rather than through a `@ViewChild` per view: the switch
     * means only one view is ever in the DOM, so one query covers all of them.
     * `setTimeout` because the new view does not exist until the pass this call
     * sits in has rendered — the same idiom the Instructions panel uses.
     *
     * A ph-* tag's focusable button or link lives inside its shadow root, so a
     * plain `querySelector().focus()` lands on the (unfocusable) host instead.
     */
    private _focusView(selector = '.back-link'): void {
        this._cdr.markForCheck();
        setTimeout(() => focusWhenMounted(() => this._el.nativeElement.querySelector<HTMLElement>(selector)));
    }

    ngOnInit(): void {
        this.loadList();

        this._exceptionSearch$
            .pipe(debounceTime(300), switchMap((q) => {
                const query = q.trim();
                if (query.length === 0) {
                    return of({ organizations: [] as GableSkillOrgLookupResult[] });
                }
                return this._service.orgLookup({ q: query, filters: this._currentFilters() })
                    .pipe(catchError(() => of({ organizations: [] as GableSkillOrgLookupResult[] })));
            }), untilDestroyed(this))
            .subscribe((res) => {
                this.exceptionSearchLoading = false;
                this.exceptionResults = res.organizations;
                this._cdr.markForCheck();
            });

        this._reachPreviewTrigger$
            .pipe(debounceTime(250), switchMap(() => {
                if (this.reach.mode !== 'selected') {
                    return of(null);
                }
                this.reachPreviewLoading = true;
                return this._service
                    .reachPreview({ filters: this._currentFilters(), exceptions: this._exceptionPayload() })
                    .pipe(catchError(() => of(null)));
            }), untilDestroyed(this))
            .subscribe((res) => {
                this.reachPreviewLoading = false;
                this.reachPreview = res;
                this._cdr.markForCheck();
            });

        this._bandCountsTrigger$
            .pipe(debounceTime(250), switchMap(() => {
                const states = this.reach.filters.states;
                const bandDefs: { key: string; sizeBands: GableSkillSizeBand[] }[] = [
                    { key: 'all', sizeBands: [] },
                    { key: 'under_100', sizeBands: ['under_100'] },
                    { key: '100_500', sizeBands: ['100_500'] },
                    { key: 'over_500', sizeBands: ['over_500'] },
                ];
                return forkJoin(bandDefs.map((b) => this._service
                    .reachPreview({ filters: { states, sizeBands: b.sizeBands, customRange: null } })
                    .pipe(
                        map((res) => ({ key: b.key, count: res.count })),
                        catchError(() => of({ key: b.key, count: 0 })),
                    )));
            }), untilDestroyed(this))
            .subscribe((results) => {
                const next: Partial<Record<string, number>> = {};
                for (const r of results) {
                    next[r.key] = r.count;
                }
                this.bandCounts = next;
                this._cdr.markForCheck();
            });
    }

    // ---------------------------------------------------------------------
    // List
    // ---------------------------------------------------------------------

    /** Flatten playbook markdown: headers become plain lines, and the tools on a "Tools used:" line render as code chips. */
    playbookBody(body: string | null | undefined): string {
        const flattened = (body ?? '').replace(/^#{1,6}\s+/gm, '');

        return flattened.replace(/^(\s*tools used:\s*)(.+?)(\.?)\s*$/gim, (_, label, list, dot) => {
            const tools = list
                .split(',')
                .map((t: string) => t.trim().replace(/^`|`$/g, ''))
                .filter((t: string) => t !== '')
                .map((t: string) => '`' + t + '`')
                .join(', ');

            return label + tools + dot;
        });
    }

    loadList(): void {
        this.loading = true;
        this.listLoadFailed = false;
        this._service.list().pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.loading = false;
                this.listLoaded = true;
                this.skills = res.skills;
                this._cdr.markForCheck();
            },
            error: () => {
                this.loading = false;
                this.listLoaded = true;
                this.listLoadFailed = true;
                this._growler.oops('Couldn\'t load skills. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    get filteredSkills(): GableSkillRow[] {
        const q = this.searchTerm.trim().toLowerCase();
        if (!q) {
            return this.skills;
        }
        return this.skills.filter((s) => s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q));
    }

    /** A live search keeps the box mounted, or there would be nothing left to clear it with. */
    get showSearch(): boolean {
        return this.skills.length > 5 || this.searchTerm.trim() !== '';
    }

    trackBySkillId(_i: number, s: GableSkillRow): number {
        return s.id;
    }

    relativeUpdated(iso: string | null): string {
        return iso ? moment(iso).fromNow() : '';
    }

    trackByStarterKey(_i: number, s: GableSkillStarter): string {
        return s.key;
    }

    trackByState(_i: number, s: { value: string }): string {
        return s.value;
    }

    trackByValue(_i: number, v: string): string {
        return v;
    }

    trackByException(_i: number, e: GableSkillReachException): number {
        return e.organizationId;
    }

    trackByOrgLookup(_i: number, o: GableSkillOrgLookupResult): number {
        return o.id;
    }

    openDetail(row: GableSkillRow): void {
        this.view = 'detail';
        this.selected = null;
        this.detailLoading = true;
        this._service.show(row.id).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.detailLoading = false;
                this.selected = res.skill;
                this._cdr.markForCheck();
            },
            error: () => {
                this.detailLoading = false;
                this._growler.oops('Couldn\'t load that skill.');
                this.view = 'list';
                this._cdr.markForCheck();
            },
        });
    }

    backToList(): void {
        this.view = 'list';
        this.selected = null;
        this.loadList();
    }

    // ---------------------------------------------------------------------
    // Add menu
    // ---------------------------------------------------------------------

    readonly addSkillMenu = [
        { value: 'upload', label: 'Upload a file', icon: 'upload-simple' },
        { value: 'gable', label: 'Write it with Gable', icon: 'sparkle' },
        { value: 'library', label: 'Browse the PayHOA library', icon: 'books' },
    ];

    onAddSkill(value: string): void {
        if (value === 'upload') {
            this.onAddUpload();
        } else if (value === 'gable') {
            this.onAddWriteWithGable();
        } else if (value === 'library') {
            this.onAddFromLibrary();
        }
    }

    onAddUpload(): void {
        this.uploadError = null;
        this.view = 'add-upload';
        this._focusView();
    }

    // ---------------------------------------------------------------------
    // Upload drop zone
    // ---------------------------------------------------------------------

    /**
     * ph-file-upload reports every file from a drop (its `multiple` attribute
     * is set for exactly this), so a drop of more than one is still caught
     * here rather than silently importing the first and discarding the rest.
     * The zone splits one drop by `accept` into `ph-files` then `ph-reject`,
     * back to back in the same task, so both are gathered here and checked
     * together once the drop's events are done; the .md check below then
     * words a wrong file instead of the zone's generic line.
     */
    onUploadFilesSelected(files: File[]): void {
        if (this._uploadDrop !== null) {
            this._uploadDrop = [...this._uploadDrop, ...files];
            return;
        }
        this._uploadDrop = [...files];
        queueMicrotask(() => {
            const dropped = this._uploadDrop ?? [];
            this._uploadDrop = null;
            this._checkUpload(dropped);
        });
    }

    private _checkUpload(files: File[]): void {
        if (files.length > 1) {
            this.uploadError = 'One file at a time — drop a single .md file and Gable will read it.';
            this._cdr.markForCheck();
            return;
        }
        const file = files[0] ?? null;
        if (file === null) {
            return;
        }
        this._acceptUpload(file, null);
    }

    onAddWriteWithGable(): void {
        this.briefText = '';
        this.view = 'add-gable';
        this._focusView();
    }

    // Cancel and the back-link both leave the compose view. If a draft is still
    // in flight when they do, the response must be dropped: `next()` below
    // switches the view to 'edit', which would yank the user out of whatever
    // they moved on to, seconds after they left.
    onLeaveDraft(): void {
        if (this.drafting) {
            this._draftAbort$.next();
            this.drafting = false;
        }
        this.view = 'list';
    }

    onDraftWithGable(): void {
        const brief = this.briefText.trim();
        if (!brief || this.drafting) {
            return;
        }
        this.drafting = true;
        this._service.draft(brief).pipe(takeUntil(this._draftAbort$), untilDestroyed(this)).subscribe({
            next: (res: GableSkillDraft) => {
                this.drafting = false;
                this.edit = { id: null, name: res.name, description: res.description, body: res.body, source: 'gable', banner: res.notes };
                this.editReach = this._blankReach();
                this.view = 'edit';
                this._cdr.markForCheck();
            },
            error: () => {
                this.drafting = false;
                this._growler.oops('Gable couldn\'t draft that skill. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    onAddFromLibrary(): void {
        this.view = 'add-library';
        this._focusView();
        this.startersLoading = true;
        this._service.starters().pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.startersLoading = false;
                this.starters = res.starters;
                this._cdr.markForCheck();
            },
            error: () => {
                this.startersLoading = false;
                this._growler.oops('Couldn\'t load the library. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    /** Matched on name, since a starter added from the library is saved under its key. */
    isStarterAdded(starter: GableSkillStarter): boolean {
        return this.skills.some((s) => s.name === starter.key);
    }

    addStarter(starter: GableSkillStarter): void {
        this.edit = { id: null, name: starter.key, description: starter.description, body: starter.body, source: 'library', banner: null };
        this.editReach = this._blankReach();
        this.view = 'edit';
    }

    triggerReplaceFile(): void {
        this.replaceInputRef?.nativeElement.click();
    }

    onReplaceFileSelected(event: Event): void {
        if (!this.selected) {
            return;
        }
        this._handleFileSelected(event, this.selected.id);
    }

    private _handleFileSelected(event: Event, targetId: number | null): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        input.value = '';
        if (!file) {
            return;
        }
        this._acceptUpload(file, targetId);
    }

    /**
     * The one gate both routes pass through. `accept=".md"` on the input is a
     * filter, not a guarantee — the picker's "All files" escape hatch and every
     * drag-and-drop bypass it — so the extension is checked here instead.
     */
    private _acceptUpload(file: File, targetId: number | null): void {
        if (!/\.md$/i.test(file.name)) {
            const message = `${file.name} is not a Markdown file. Skills are .md — `
                + 'save or rename the file, then try again.';
            // `uploadError` is rendered only inside the add-upload view. The
            // replace-file route (`targetId !== null`) is driven from the detail
            // view's kebab and never navigates there, so the message had nowhere
            // to appear and refusing a file looked like nothing happening.
            if (targetId !== null) {
                this._growler.oops(message);
            } else {
                this.uploadError = message;
            }
            this._cdr.markForCheck();
            return;
        }
        this.uploadError = null;
        this._readFile(file, targetId);
    }

    private _readFile(file: File, targetId: number | null): void {
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result ?? '');
            const parsed = this._parseFrontmatter(text);
            const name = parsed.name || file.name.replace(/\.md$/i, '');
            this.edit = {
                id: targetId,
                name,
                description: parsed.description ?? '',
                body: parsed.body,
                source: 'upload',
                banner: `Read ${file.name}: name '${name}', description ${parsed.description ? 'from front matter' : 'not found'}.`,
            };
            this.editReach = targetId !== null && this.selected ? this._cloneReach(this.selected.reach) : this._blankReach();
            this.view = 'edit';
            // The drop zone this view replaces may be holding focus.
            this._focusView('#skillNameInput');
            this._cdr.markForCheck();
        };
        // A file can become unreadable between the pick and the read — a cloud
        // placeholder that never hydrates, an unplugged volume, a move. Without
        // this the view never changes and no message appears, which is the one
        // silent hole left in the gate above.
        reader.onerror = () => {
            const message = `Couldn't read ${file.name}. The file may have moved — try choosing it again.`;
            if (targetId !== null) {
                this._growler.oops(message);
            } else {
                this.uploadError = message;
            }
            this._cdr.markForCheck();
        };
        reader.readAsText(file);
    }

    private _parseFrontmatter(text: string): { name: string; description: string | null; body: string } {
        const match = text.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
        if (!match) {
            return { name: '', description: null, body: text.trim() };
        }
        const [, frontmatter, body] = match;
        let name = '';
        let description: string | null = null;
        for (const line of frontmatter.split(/\r?\n/)) {
            const idx = line.indexOf(':');
            if (idx === -1) {
                continue;
            }
            const key = line.slice(0, idx).trim().toLowerCase();
            const value = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
            if (key === 'name') {
                name = value;
            } else if (key === 'description') {
                description = value;
            }
        }
        return { name, description, body: body.trim() };
    }

    // ---------------------------------------------------------------------
    // Detail
    // ---------------------------------------------------------------------

    toggleEnabled(): void {
        if (!this.selected) {
            return;
        }
        const target = this.selected;
        this._service.update(target.id, { enabled: !target.enabled }).pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.selected = res.skill;
                this.skillsChanged.emit();
                this._cdr.markForCheck();
            },
            error: () => {
                this._growler.oops('Couldn\'t change that. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    /** `focusSelector` is the field the editor opens on: the Instructions card's Edit lands in the instructions box. */
    openEdit(focusSelector = '.back-link'): void {
        if (!this.selected) {
            return;
        }
        this.edit = {
            id: this.selected.id,
            name: this.selected.name,
            description: this.selected.description ?? '',
            body: this.selected.body,
            source: this.selected.source,
            banner: null,
        };
        this.editReach = this._cloneReach(this.selected.reach);
        this.view = 'edit';
        this._focusView(focusSelector);
    }

    tryInChat(): void {
        if (!this.selected) {
            return;
        }
        const prompt = `Run the "${this.selected.name}" skill against my current real cases and tell me what you'd draft. Don't send or change anything yet — just show me.`;
        this._drawer.startNewChatWithMessage(prompt);
        this.switchToChat.emit();
    }

    editWithGable(): void {
        if (!this.selected) {
            return;
        }
        // Fire the turn immediately — Gable loads the skill itself and interviews the user.
        const prompt = `I want to edit the "${this.selected.name}" skill. Load it, then interview me — one question at a time — about what should change. Once we agree, show me the full revised markdown and save it only after I confirm.`;
        this._drawer.startNewChatWithMessage(prompt);
        this.switchToChat.emit();
    }

    downloadSkill(): void {
        if (!this.selected) {
            return;
        }
        const frontmatter = `---\nname: ${this.selected.name}\ndescription: ${this.selected.description ?? ''}\n---\n\n`;
        const blob = new Blob([frontmatter + this.selected.body], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${this.selected.name}.md`;
        anchor.click();
        URL.revokeObjectURL(url);
    }

    confirmRemove(): void {
        if (!this.selected) {
            return;
        }
        this.removeConfirmOpen = true;
    }

    onRemoveConfirmClosed(confirmed: boolean): void {
        this.removeConfirmOpen = false;
        if (!confirmed || !this.selected) {
            return;
        }
        const target = this.selected;
        this._service.remove(target.id).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this._growler.success('Removed', `"${target.name}" was removed.`);
                this.skillsChanged.emit();
                this.backToList();
            },
            error: () => {
                this._growler.oops('Couldn\'t remove that skill. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    /** Dispatches the detail view's kebab menu, whose items are data for ph-dropdown. */
    onSkillMenuSelect(value: string): void {
        switch (value) {
            case 'toggle':
                this.toggleEnabled();
                break;
            case 'chat':
                this.tryInChat();
                break;
            case 'edit-gable':
                this.editWithGable();
                break;
            case 'replace':
                this.triggerReplaceFile();
                break;
            case 'download':
                this.downloadSkill();
                break;
            case 'remove':
                this.confirmRemove();
                break;
        }
    }

    // ---------------------------------------------------------------------
    // Edit
    // ---------------------------------------------------------------------

    get fileNamePreview(): string {
        return `${this.edit.name.trim() || 'skill-name'}.md`;
    }

    get canSaveEdit(): boolean {
        return NAME_PATTERN.test(this.edit.name.trim()) && this.edit.body.trim().length > 0;
    }

    get editReachSummaryText(): string {
        return this._describeReach(this.editReach);
    }

    cancelEdit(): void {
        const wasExisting = this.edit.id !== null;
        this.view = wasExisting ? 'detail' : 'list';
        if (!wasExisting) {
            this.loadList();
        }
    }

    saveEdit(): void {
        if (!this.canSaveEdit || this.saving) {
            return;
        }
        this.saving = true;
        const body: GableSkillInput = {
            name: this.edit.name.trim(),
            description: this.edit.description.trim() ? this.edit.description.trim() : null,
            body: this.edit.body,
            reachMode: this.editReach.mode,
            reachFilters: this.editReach.filters,
            reachExceptions: this.editReach.exceptions.map((e) => ({ organizationId: e.organizationId, type: e.type })),
        };
        if (this.edit.id === null) {
            body.source = this.edit.source;
        }
        const request$ = this.edit.id === null
            ? this._service.create(body)
            : this._service.update(this.edit.id, body);
        request$.pipe(untilDestroyed(this)).subscribe({
            next: (res) => {
                this.saving = false;
                this.selected = res.skill;
                this.view = 'detail';
                this.skillsChanged.emit();
                this._growler.success('Saved', `"${res.skill.name}" was saved.`);
                this._cdr.markForCheck();
            },
            error: (err: { error?: { message?: string } }) => {
                this.saving = false;
                this._growler.oops(err?.error?.message ?? 'Couldn\'t save that skill. Please try again.');
                this._cdr.markForCheck();
            },
        });
    }

    // ---------------------------------------------------------------------
    // Reach editor
    // ---------------------------------------------------------------------

    openReachEditor(origin: 'edit' | 'detail'): void {
        this._reachOrigin = origin;
        const source = origin === 'detail' ? this.selected?.reach : this.editReach;
        this.reach = source ? this._cloneReach(source) : this._blankReach();
        this.reachPreview = null;
        this.reachConfirmZero = false;
        this.statesExpanded = false;
        this.stateSearch = '';
        this.exceptionSearch = '';
        this.exceptionResults = [];
        this.view = 'reach';
        if (this.reach.mode === 'selected') {
            this._bandCountsTrigger$.next();
            this._reachPreviewTrigger$.next();
        }
    }

    cancelReach(): void {
        this.view = this._reachOrigin;
    }

    get canSaveReach(): boolean {
        if (this.reach.mode === 'all') {
            return true;
        }
        if (this.reachPreviewLoading) {
            return false;
        }
        return (this.reachPreview?.count ?? 0) > 0 || this.reachConfirmZero;
    }

    saveReach(): void {
        if (!this.canSaveReach) {
            return;
        }
        if (this._reachOrigin === 'detail' && this.selected) {
            const payload: GableSkillInput = {
                reachMode: this.reach.mode,
                reachFilters: this._currentFilters(),
                reachExceptions: this._exceptionPayload(),
            };
            this._service.update(this.selected.id, payload).pipe(untilDestroyed(this)).subscribe({
                next: (res) => {
                    this.selected = res.skill;
                    this.view = 'detail';
                    this.skillsChanged.emit();
                    this._growler.success('Saved', 'Reach updated.');
                    this._cdr.markForCheck();
                },
                error: () => {
                    this._growler.oops('Couldn\'t update reach. Please try again.');
                    this._cdr.markForCheck();
                },
            });
            return;
        }
        this.editReach = this._cloneReach(this.reach);
        this.view = 'edit';
    }

    setReachMode(mode: GableSkillReachMode): void {
        this.reach.mode = mode;
        if (mode === 'selected') {
            this._bandCountsTrigger$.next();
            this._reachPreviewTrigger$.next();
        } else {
            this.reachPreview = null;
        }
    }

    isStateSelected(code: string): boolean {
        return this.reach.filters.states.includes(code);
    }

    stateLabel(code: string): string {
        return US_STATES.find((s) => s.value === code)?.label ?? code;
    }

    get filteredStates(): { value: string; label: string }[] {
        const q = this.stateSearch.trim().toLowerCase();
        if (!q) {
            return US_STATES;
        }
        return US_STATES.filter((s) => s.label.toLowerCase().includes(q) || s.value.toLowerCase().includes(q));
    }

    toggleState(code: string): void {
        const states = this.reach.filters.states;
        this.reach.filters.states = states.includes(code) ? states.filter((s) => s !== code) : [...states, code];
        this._onReachFiltersChanged();
    }

    removeState(code: string): void {
        this.reach.filters.states = this.reach.filters.states.filter((s) => s !== code);
        this._onReachFiltersChanged();
    }

    isEveryOrgBand(): boolean {
        return this.reach.filters.sizeBands.length === 0 && this.reach.filters.customRange === null;
    }

    hasSizeBand(band: GableSkillSizeBand): boolean {
        return this.reach.filters.sizeBands.includes(band);
    }

    setSizeAll(): void {
        this.reach.filters.sizeBands = [];
        this.reach.filters.customRange = null;
        this._onReachFiltersChanged();
    }

    toggleSizeBand(band: GableSkillSizeBand): void {
        this.reach.filters.customRange = null;
        const bands = this.reach.filters.sizeBands;
        this.reach.filters.sizeBands = bands.includes(band) ? bands.filter((b) => b !== band) : [...bands, band];
        this._onReachFiltersChanged();
    }

    setSizeCustom(): void {
        this.reach.filters.sizeBands = [];
        this.reach.filters.customRange = this.reach.filters.customRange ?? { min: 0, max: 100 };
        this._onReachFiltersChanged();
    }

    onReachChanged(): void {
        this._onReachFiltersChanged();
    }

    private _onReachFiltersChanged(): void {
        this.reachConfirmZero = false;
        // Blocks canSaveReach against the stale count during the debounce window.
        this.reachPreviewLoading = this.reach.mode === 'selected';
        this.reachPreview = null;
        this._bandCountsTrigger$.next();
        this._reachPreviewTrigger$.next();
    }

    onExceptionSearchChange(q: string): void {
        this.exceptionSearchLoading = q.trim().length > 0;
        this._exceptionSearch$.next(q);
    }

    addException(org: GableSkillOrgLookupResult, type: GableSkillExceptionType): void {
        this.reach.exceptions = [
            ...this.reach.exceptions.filter((e) => e.organizationId !== org.id),
            { organizationId: org.id, name: org.name, type },
        ];
        this.exceptionResults = this.exceptionResults.filter((o) => o.id !== org.id);
        this._reachPreviewTrigger$.next();
    }

    removeException(ex: GableSkillReachException): void {
        this.reach.exceptions = this.reach.exceptions.filter((e) => e.organizationId !== ex.organizationId);
        this._reachPreviewTrigger$.next();
    }

    bandLabel(band: GableSkillSizeBand): string {
        switch (band) {
            case 'under_100':
                return 'under 100 units';
            case '100_500':
                return '100–500 units';
            case 'over_500':
                return 'over 500 units';
        }
    }

    // ---------------------------------------------------------------------
    // Shared helpers
    // ---------------------------------------------------------------------

    shortDate(iso: string | null | undefined): string {
        return iso ? moment(iso).format('MMM D, YYYY') : '';
    }

    private _currentFilters(): GableSkillReachFilters {
        return {
            states: this.reach.filters.states,
            sizeBands: this.reach.filters.sizeBands,
            customRange: this.reach.filters.customRange,
        };
    }

    private _exceptionPayload(): Array<{ organizationId: number; type: GableSkillExceptionType }> {
        return this.reach.exceptions.map((e) => ({ organizationId: e.organizationId, type: e.type }));
    }

    private _describeReach(r: GableSkillReach): string {
        if (r.mode === 'all') {
            return 'All organizations';
        }
        const parts = [
            r.filters.states.length > 0
                ? `${r.filters.states.length} state${r.filters.states.length === 1 ? '' : 's'}`
                : null,
            r.filters.customRange
                ? `${r.filters.customRange.min}–${r.filters.customRange.max} units`
                : (r.filters.sizeBands.length > 0 ? r.filters.sizeBands.map((b) => this.bandLabel(b)).join(', ') : null),
        ].filter((p): p is string => p !== null);
        let base = parts.length > 0 ? `Selected organizations (${parts.join(' · ')})` : 'Selected organizations';
        if (r.exceptions.length > 0) {
            base += ` · ${r.exceptions.length} exception${r.exceptions.length === 1 ? '' : 's'}`;
        }
        return base;
    }

    private _blankDraft(): SkillEditDraft {
        return { id: null, name: '', description: '', body: '', source: 'manual', banner: null };
    }

    private _blankReach(): GableSkillReach {
        return { mode: 'all', filters: { states: [], sizeBands: [], customRange: null }, exceptions: [] };
    }

    private _cloneReach(r: GableSkillReach): GableSkillReach {
        return {
            mode: r.mode,
            filters: {
                states: [...r.filters.states],
                sizeBands: [...r.filters.sizeBands],
                customRange: r.filters.customRange ? { ...r.filters.customRange } : null,
            },
            exceptions: r.exceptions.map((e) => ({ ...e })),
        };
    }
}
